import React from "react";
import axios from "axios";
import { withSnackbar } from "notistack";
import { PropTypes } from "prop-types";
import {
	Button,
	Container,
	Grid,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
	withStyles,
} from "@material-ui/core";

const useStyles = (theme) => ({
	container: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(4),
	},
	paper: {
		padding: theme.spacing(2),
		display: "flex",
		flexDirection: "column",
		overflow: "auto",
	},
	sectionTitle: {
		fontWeight: "bold",
		marginBottom: theme.spacing(1),
	},
	row: {
		display: "flex",
		alignItems: "center",
		flexWrap: "wrap",
		gap: theme.spacing(2),
	},
	action: {
		marginTop: theme.spacing(1),
	},
	field: {
		width: 200,
	},
	summary: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(3),
		marginTop: theme.spacing(1),
	},
	stat: {
		minWidth: 140,
	},
	statLabel: {
		color: "#666",
		fontSize: 12,
		textTransform: "uppercase",
	},
	warn: {
		color: "#b26500",
	},
	bad: {
		color: "#c62828",
	},
	good: {
		color: "#2e7d32",
	},
	mono: {
		fontFamily: "monospace",
		fontSize: 12,
	},
	note: {
		color: "#666",
		marginTop: theme.spacing(1),
	},
});

const FRIEND_ROW_LIMIT = 100;

const Stat = ({ classes, label, value, tone }) => (
	<div className={classes.stat}>
		<div className={classes.statLabel}>{label}</div>
		<Typography variant="body1" className={tone ? classes[tone] : undefined}>
			{value}
		</Typography>
	</div>
);

const BuddiesView = ({ classes, dev, enqueueSnackbar }) => {
	const [user, setUser] = React.useState("");
	const [data, setData] = React.useState(null);
	const [busy, setBusy] = React.useState(false);

	const [friendCount, setFriendCount] = React.useState(10);
	const [removeCount, setRemoveCount] = React.useState(0);

	const [senderUser, setSenderUser] = React.useState("");
	const [requestCount, setRequestCount] = React.useState(1);
	const [ttlSeconds, setTtlSeconds] = React.useState(0);

	const reportError = (err) => {
		const message =
			(err.response && err.response.data && err.response.data.error) ||
			err.message ||
			"request failed";
		enqueueSnackbar(message, { variant: "error" });
	};

	const inspect = (target) => {
		const id = (target === undefined ? user : target).trim();
		if (id === "") {
			enqueueSnackbar("Enter a game code or user id", { variant: "warning" });
			return Promise.resolve();
		}

		setBusy(true);
		return axios
			.get("/api/dev/" + dev + "/buddies/inspect/" + encodeURIComponent(id))
			.then((json) => {
				setData(json.data);
			})
			.catch(reportError)
			.finally(() => setBusy(false));
	};

	// Every mutation re-reads the user afterwards, so the panel always shows the
	// state the servers would see rather than what the UI just asked for.
	const mutate = (method, endpoint, payload, successMessage) => {
		if (user.trim() === "") {
			enqueueSnackbar("Enter a game code or user id", { variant: "warning" });
			return;
		}

		const body = Object.assign({ user: user.trim() }, payload);
		const url = "/api/dev/" + dev + "/buddies/" + endpoint;
		const request =
			method === "delete"
				? axios.delete(url, { data: body })
				: axios.post(url, body);

		setBusy(true);
		request
			.then((json) => {
				enqueueSnackbar(successMessage(json.data), { variant: "success" });
				if (json.data.warning) {
					enqueueSnackbar(json.data.warning, { variant: "warning" });
				}
				return inspect();
			})
			.catch(reportError)
			.finally(() => setBusy(false));
	};

	const addFriends = () =>
		mutate(
			"post",
			"pseudo-friends",
			{ count: Number(friendCount) },
			(res) =>
				"Added " +
				res.changed +
				" pseudo friends — app friends now " +
				res.appFriendsCount +
				"/" +
				res.buddyLimit
		);

	const removeFriends = () =>
		mutate(
			"delete",
			"pseudo-friends",
			{ count: Number(removeCount) },
			(res) =>
				"Removed " +
				res.changed +
				" pseudo friends — app friends now " +
				res.appFriendsCount +
				"/" +
				res.buddyLimit
		);

	const seedRequests = () =>
		mutate(
			"post",
			"requests",
			{
				senderUser: senderUser.trim(),
				count: Number(requestCount),
				ttlSeconds: Number(ttlSeconds),
			},
			(res) =>
				"Seeded " +
				res.seeded +
				" request(s) — inbox holds " +
				res.fieldCount +
				" (maxStoredRequests " +
				res.maxStoredRequests +
				")"
		);

	const deleteRequest = (senderId) =>
		mutate("delete", "requests", { senderId: senderId }, (res) =>
			res.cleared
				? "Cleared the inbox (" + res.removed + " card(s))"
				: "Removed " + res.removed + " card"
		);

	const userInfo = data && data.user;
	const config = data && data.config;
	const inbox = data && data.inbox;
	const friends = (data && data.friends) || [];
	const receiptStale =
		userInfo &&
		userInfo.receiptFound &&
		userInfo.receiptAppFriendsCount !== userInfo.appFriendsCount;

	return (
		<Container maxWidth="lg" className={classes.container}>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Paper className={classes.paper}>
						<Typography variant="h6" className={classes.sectionTitle}>
							Buddies QA
						</Typography>
						<div className={classes.row}>
							<TextField
								label="Target user — game code or user id"
								variant="outlined"
								size="small"
								style={{ width: 320 }}
								helperText="Friends are added to this user, and seeded requests land in this user's inbox"
								value={user}
								onChange={(ev) => setUser(ev.target.value)}
								onKeyPress={(ev) => {
									if (ev.key === "Enter") {
										inspect();
									}
								}}
							/>
							<Button
								variant="contained"
								color="primary"
								disabled={busy}
								onClick={() => inspect()}
							>
								Load
							</Button>
						</div>

						{userInfo && (
							<div className={classes.summary}>
								<Stat
									classes={classes}
									label="User id"
									value={<span className={classes.mono}>{userInfo.userId}</span>}
								/>
								<Stat
									classes={classes}
									label="Game code"
									value={userInfo.gameCode || "—"}
								/>
								<Stat
									classes={classes}
									label="Name"
									value={userInfo.displayName || "—"}
								/>
								<Stat
									classes={classes}
									label="Snuid"
									value={userInfo.snuid || "not linked"}
									tone={userInfo.snuid ? undefined : "bad"}
								/>
								<Stat
									classes={classes}
									label="Vip tier"
									value={userInfo.vipType}
								/>
								<Stat
									classes={classes}
									label="App friends"
									value={
										userInfo.appFriendsCount +
										" / " +
										userInfo.buddyLimit +
										(userInfo.atBuddyLimit ? " (at limit)" : "")
									}
									tone={userInfo.atBuddyLimit ? "warn" : "good"}
								/>
								<Stat
									classes={classes}
									label="Pseudo friends"
									value={userInfo.pseudoFriendCount}
								/>
								<Stat
									classes={classes}
									label="Receipt (in-game check)"
									value={
										userInfo.receiptFound
											? userInfo.receiptAppFriendsCount +
											  " / " +
											  userInfo.receiptBuddyLimit
											: "no cached receipt"
									}
									tone={receiptStale ? "warn" : undefined}
								/>
							</div>
						)}

						{userInfo && receiptStale && (
							<Typography variant="body2" className={classes.warn}>
								The cached receipt disagrees with mongo. The gameserver's
								send-side limit check reads the receipt, so it will keep using{" "}
								{userInfo.receiptAppFriendsCount} until the user logs in again.
							</Typography>
						)}

						{config && (
							<Typography variant="body2" className={classes.note}>
								buddyRequestConfig — pendingRequestTTLSeconds{" "}
								{config.pendingRequestTtlSeconds}, maxStoredRequests{" "}
								{config.maxStoredRequests}
							</Typography>
						)}
					</Paper>
				</Grid>

				<Grid item xs={12} md={6}>
					<Paper className={classes.paper}>
						<Typography variant="h6" className={classes.sectionTitle}>
							Pseudo Facebook friends
						</Typography>
						<Typography variant="body2" className={classes.note}>
							Writes <span className={classes.mono}>3_&lt;snuid&gt;</span>{" "}
							entries into the user's app friends, which is what the buddy limit
							is counted off, and refreshes the cached receipt the in-game check
							reads. Snuids start with <span className={classes.mono}>9999</span>{" "}
							so removal never touches real friends.
						</Typography>
						<div className={classes.row} style={{ marginTop: 12 }}>
							<TextField
								label="Count"
								type="number"
								variant="outlined"
								size="small"
								className={classes.field}
								value={friendCount}
								onChange={(ev) => setFriendCount(ev.target.value)}
							/>
						</div>
						<Typography variant="body2" className={classes.note}>
							Each friend is an id plus a cached receipt — enough for the limit
							and the client's friend list, and nothing else. Max 500 per click.
							For a genuine two-sided friendship, seed a request from a pseudo
							sender and accept it: the accept path writes both halves itself.
						</Typography>
						<div className={classes.row + " " + classes.action}>
							<Button
								variant="contained"
								color="primary"
								disabled={busy}
								onClick={addFriends}
							>
								Add friends
							</Button>
							<TextField
								label="Remove (0 = all pseudo)"
								type="number"
								variant="outlined"
								size="small"
								className={classes.field}
								value={removeCount}
								onChange={(ev) => setRemoveCount(ev.target.value)}
							/>
							<Button variant="contained" disabled={busy} onClick={removeFriends}>
								Remove pseudo
							</Button>
						</div>
					</Paper>
				</Grid>

				<Grid item xs={12} md={6}>
					<Paper className={classes.paper}>
						<Typography variant="h6" className={classes.sectionTitle}>
							Seed a pending request
						</Typography>
						<Typography variant="body2" className={classes.note}>
							Writes cards into{" "}
							<span className={classes.mono}>
								{inbox ? inbox.key : "br:<userID>"}
							</span>{" "}
							the way a game room does, so accept / reject / expiry can be tested
							without two clients in a match. No eviction is applied — seed past
							maxStoredRequests on purpose to see what the read path does.
						</Typography>
						<Typography variant="body2" className={classes.warn}>
							The target user loaded above is the <strong>receiver</strong>; the
							sender below is who the request comes from. The receiver only sees
							the card in their next login payload, so restart their client
							afterwards.
						</Typography>
						<div className={classes.row} style={{ marginTop: 12 }}>
							<TextField
								label="Sender (game code / user id)"
								variant="outlined"
								size="small"
								style={{ width: 260 }}
								value={senderUser}
								onChange={(ev) => setSenderUser(ev.target.value)}
								helperText="Empty mints pseudo senders instead"
							/>
							<TextField
								label="Pseudo senders"
								type="number"
								variant="outlined"
								size="small"
								className={classes.field}
								value={requestCount}
								onChange={(ev) => setRequestCount(ev.target.value)}
								disabled={senderUser.trim() !== ""}
							/>
							<TextField
								label="TTL seconds (0 = config)"
								type="number"
								variant="outlined"
								size="small"
								className={classes.field}
								value={ttlSeconds}
								onChange={(ev) => setTtlSeconds(ev.target.value)}
							/>
						</div>
						<div className={classes.row + " " + classes.action}>
							<Button
								variant="contained"
								color="primary"
								disabled={busy}
								onClick={seedRequests}
							>
								Seed request(s)
							</Button>
							<Button
								variant="contained"
								disabled={busy || !inbox || inbox.fieldCount === 0}
								onClick={() => deleteRequest("")}
							>
								Clear inbox
							</Button>
						</div>
					</Paper>
				</Grid>

				{inbox && (
					<Grid item xs={12}>
						<Paper className={classes.paper}>
							<Typography variant="h6" className={classes.sectionTitle}>
								Pending requests — {inbox.key} ({inbox.fieldCount} field
								{inbox.fieldCount === 1 ? "" : "s"}, key ttl{" "}
								{inbox.keyTtlSeconds}s)
							</Typography>
							{inbox.requests.length === 0 ? (
								<Typography variant="body2" className={classes.note}>
									The inbox is empty.
								</Typography>
							) : (
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Sender id</TableCell>
											<TableCell>Game code</TableCell>
											<TableCell>Snuid</TableCell>
											<TableCell>Name</TableCell>
											<TableCell>Avatar</TableCell>
											<TableCell>Expires in</TableCell>
											<TableCell>State</TableCell>
											<TableCell>Raw value</TableCell>
											<TableCell />
										</TableRow>
									</TableHead>
									<TableBody>
										{inbox.requests.map((request) => (
											<TableRow key={request.senderId}>
												<TableCell className={classes.mono}>
													{request.senderId}
												</TableCell>
												<TableCell>
													{request.senderFound ? request.senderGameCode : "—"}
												</TableCell>
												<TableCell>
													{request.senderFound ? (
														request.senderSnuid || (
															<span className={classes.bad}>none</span>
														)
													) : (
														<span className={classes.bad}>user missing</span>
													)}
												</TableCell>
												<TableCell>{request.name}</TableCell>
												<TableCell>{request.avatarId}</TableCell>
												<TableCell>{request.expiresIn}s</TableCell>
												<TableCell>
													{!request.decoded ? (
														<span className={classes.bad}>undecodable</span>
													) : !request.live ? (
														<span className={classes.bad}>expired</span>
													) : !request.clientVisible ? (
														<span className={classes.warn}>
															hidden (expiry buffer)
														</span>
													) : (
														<span className={classes.good}>live</span>
													)}
												</TableCell>
												<TableCell className={classes.mono}>
													{request.raw}
												</TableCell>
												<TableCell>
													<Button
														size="small"
														disabled={busy}
														onClick={() => deleteRequest(request.senderId)}
													>
														Delete
													</Button>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}
						</Paper>
					</Grid>
				)}

				{data && (
					<Grid item xs={12}>
						<Paper className={classes.paper}>
							<Typography variant="h6" className={classes.sectionTitle}>
								App friends ({friends.length})
							</Typography>
							{friends.length === 0 ? (
								<Typography variant="body2" className={classes.note}>
									No app friends — nothing counts against the buddy limit yet.
								</Typography>
							) : (
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Key</TableCell>
											<TableCell>Friend user id</TableCell>
											<TableCell>Name</TableCell>
											<TableCell>Avatar</TableCell>
											<TableCell>Origin</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{friends.slice(0, FRIEND_ROW_LIMIT).map((friend) => (
											<TableRow key={friend.key}>
												<TableCell className={classes.mono}>
													{friend.key}
												</TableCell>
												<TableCell className={classes.mono}>
													{friend.userId}
												</TableCell>
												<TableCell>{friend.displayName}</TableCell>
												<TableCell>{friend.avatarId}</TableCell>
												<TableCell>
													{friend.pseudo ? "pseudo (QA)" : "real"}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}
							{friends.length > FRIEND_ROW_LIMIT && (
								<Typography variant="body2" className={classes.note}>
									Showing the first {FRIEND_ROW_LIMIT} of {friends.length}.
								</Typography>
							)}
						</Paper>
					</Grid>
				)}
			</Grid>
		</Container>
	);
};

BuddiesView.propTypes = {
	classes: PropTypes.object.isRequired,
};

export default withStyles(useStyles)(withSnackbar(BuddiesView));
